import React, { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { User, Mail, LogOut, Save, Settings as SettingsIcon } from 'lucide-react'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'
import { useNotification } from '../context/NotificationContext'
import apiClient from '../services/api'

/**
 * Settings page component for user profile and app preferences
 * Features profile section with Auth0 user info and app preferences stored locally
 * Includes logout functionality and follows mobile-first design patterns
 */
const SettingsPage = () => {
  const { user, logout, isLoading } = useAuth0()
  const { showSuccess, showError } = useNotification()
  
  // Profile state (from API and Auth0)
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    picture: ''
  })
  
  // Display name for UI (combined first_name + last_name)
  const [displayName, setDisplayName] = useState('')
  
  // App preferences state (local storage)
  const [preferences, setPreferences] = useState({
    theme: 'light',
    sidebarCollapsed: false,
    emailNotifications: true
  })
  
  // Loading state for save operations
  const [isSaving, setIsSaving] = useState(false)

  // Initialize data from API and Auth0
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        // Load profile from API
        const apiProfile = await apiClient.getUserProfile()
        setProfileData({
          first_name: apiProfile.first_name || '',
          last_name: apiProfile.last_name || '',
          email: apiProfile.email || '',
          picture: user?.picture || '' // Picture comes from Auth0
        })
        
        // Set display name for UI
        const fullName = `${apiProfile.first_name || ''} ${apiProfile.last_name || ''}`.trim()
        setDisplayName(fullName || user?.name || '')
      } catch (error) {
        console.error('Failed to load profile from API:', error)
        // Fallback to Auth0 data if API fails
        if (user) {
          const nameParts = (user.name || '').split(' ')
          setProfileData({
            first_name: nameParts[0] || '',
            last_name: nameParts.slice(1).join(' ') || '',
            email: user.email || '',
            picture: user.picture || ''
          })
          setDisplayName(user.name || '')
        }
      }
    }

    if (user) {
      loadProfileData()
    }
    
    // Load app preferences from local storage
    const savedPreferences = localStorage.getItem('appPreferences')
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences))
      } catch (error) {
        console.error('Failed to load preferences:', error)
      }
    }
  }, [user])

  // Handle profile changes
  const handleProfileChange = (field, value) => {
    setProfileData(prev => {
      const updated = {
        ...prev,
        [field]: value
      }
      
      // Update display name when first_name or last_name changes
      if (field === 'first_name' || field === 'last_name') {
        const fullName = `${updated.first_name || ''} ${updated.last_name || ''}`.trim()
        setDisplayName(fullName)
      }
      
      return updated
    })
  }

  // Handle preferences changes
  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Save profile data to API
  const handleSaveProfile = async () => {
    setIsSaving(true)
    try {
      // Prepare data for API (only send fields that can be updated)
      const updateData = {
        email: profileData.email,
        first_name: profileData.first_name,
        last_name: profileData.last_name
      }
      
      // Call API to update profile
      await apiClient.updateUserProfile(updateData)
      showSuccess('Profile updated successfully', 'success')
    } catch (error) {
      console.error('Failed to update profile:', error)
      showError('Failed to update profile. Please try again.', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  // Save preferences to local storage
  const handleSavePreferences = () => {
    try {
      localStorage.setItem('appPreferences', JSON.stringify(preferences))
      showSuccess('Preferences saved successfully', 'success')
    } catch (error) {
      showError('Failed to save preferences', 'error')
    }
  }

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      })
    } catch (error) {
      console.error('Logout failed:', error)
      showError('Logout failed', 'error')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      {/* Page Header */}
      <div className="flex items-center space-x-3 mb-6">
        <SettingsIcon className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your profile and app preferences</p>
        </div>
      </div>

      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
        {/* Profile Section */}
        <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <User className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
          </div>
          
          <div className="space-y-4">
            {/* Profile Picture */}
            {/* <div className="flex items-center space-x-4">
              <div className="relative">
                {profileData.picture ? (
                  <img
                    src={profileData.picture}
                    alt="Profile"
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Profile Picture</p>
              </div>
            </div> */}

            {/* First Name Field */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                value={profileData.first_name}
                onChange={(e) => handleProfileChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your first name"
              />
            </div>

            {/* Last Name Field */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                value={profileData.last_name}
                onChange={(e) => handleProfileChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your last name"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Save Profile Button */}
            <div className="pt-4">
              <Button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="w-full sm:w-auto"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </div>
        </Card>

        {/* App Preferences Section */}
        {/* <Card className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <SettingsIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">App Preferences</h2>
          </div>
          
          <div className="space-y-4"> */}
            {/* Theme Preference */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme
              </label>
              <select
                value={preferences.theme}
                onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
            </div> */}

            {/* Sidebar Preference */}
            {/* <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Collapse Sidebar by Default
                </label>
                <p className="text-xs text-gray-500">Start with sidebar collapsed</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.sidebarCollapsed}
                  onChange={(e) => handlePreferenceChange('sidebarCollapsed', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div> */}

            {/* Email Notifications */}
            {/* <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Email Notifications
                </label>
                <p className="text-xs text-gray-500">Receive email updates</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.emailNotifications}
                  onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div> */}

            {/* Local Storage Notice */}
            {/* <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> App preferences are stored locally in your browser and will be lost if you clear your browser data.
              </p>
            </div> */}

            {/* Save Preferences Button */}
            {/* <div className="pt-4">
              <Button
                onClick={handleSavePreferences}
                className="w-full sm:w-auto"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Preferences
              </Button>
            </div>
          </div> */}
        {/* </Card>
        </div> */}

      {/* Logout Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Account Actions</h3>
            <p className="text-sm text-gray-600">Sign out of your account</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default SettingsPage
